import { Query } from '@apollo/client/react/components';
import React, { useState } from 'react';
import { compose, withProps } from 'recompose';
import get from 'lodash/get';
import { RouterlessModal } from '@/se/components/Modal';
import { breakpoint } from '@/se/utilities/responsive';
import { useMutation } from '@apollo/client';
import ObjectInput from '@/se/components/inputs/ObjectInput';
import Form from '@/se/components/Form';
import { bookingConfigurationQuery, bookingConfigurationMutation } from '../../../graph/bookings';
import { CenteredSpinner } from '@/se/components/Spinner';
import { withLabel } from '@/se/components/Label';
import SelectInput from '@/se/components/inputs/SelectInput';
import IconButton from '@material-ui/core/IconButton';
import SettingsIcon from '@material-ui/icons/Settings';
import { Box } from '@material-ui/core';
import NumberInput from '@/se/components/inputs/NumberInput';

const generateTimeOptions = () => {
  const options = {};
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      const displayString =
        hour === 0 && minute === 0
          ? 'Midnight'
          : hour === 12 && minute === 0
            ? 'Noon'
            : hour < 12
              ? `${hour === 0 ? 12 : hour}:${minute.toString().padStart(2, '0')} AM`
              : `${hour === 12 ? 12 : hour - 12}:${minute.toString().padStart(2, '0')} PM`;

      options[timeString] = displayString;
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const BlockScheduleConfigInput = withProps({
  schema: {
    dayStartTime: compose(
      withLabel('Day Start Time'),
      withProps({
        options: timeOptions,
        defaultValue: '05:00',
      })
    )(SelectInput),
    dayEndTime: compose(
      withLabel('Day End Time'),
      withProps({
        options: timeOptions,
        defaultValue: '17:00',
      })
    )(SelectInput),
    turnoverTime: compose(
      withLabel('Turnover Time (in minutes)'),
      withProps({
        options: timeOptions,
        defaultValue: 0,
      })
    )(NumberInput),
  },
  render: ({ dayStartTime, dayEndTime, turnoverTime }) => (
    <Box display="flex" flexDirection="column" style={{ gap: '1rem' }}>
      <Box>{dayStartTime}</Box>
      <Box>{dayEndTime}</Box>
      <Box>{turnoverTime}</Box>
    </Box>
  ),
  initialValue: { dayStartTime: '05:00', dayEndTime: '17:00', turnoverTime: 0 },
})(ObjectInput);

const mapInput = (val = {}) => ({
  dayStartTime: get(val, 'dayStartTime', '05:00'),
  dayEndTime: get(val, 'dayEndTime', '17:00'),
  turnoverTime: get(val, 'turnoverTime', 0),
});

const BlockScheduleConfig = ({ style }) => {
  const [open, setOpen] = useState(false);
  const [formKey, setFormKey] = useState(0);

  const [mutate] = useMutation(bookingConfigurationMutation);

  const handleOpen = () => {
    setFormKey(prev => prev + 1);
    setOpen(true);
  };

  const handleSubmit = async values => {
    const result = await mutate({
      variables: {
        dayStartTime: get(values, 'dayStartTime'),
        dayEndTime: get(values, 'dayEndTime'),
        turnoverTime: get(values, 'turnoverTime'),
      },
      refetchQueries: [{ query: bookingConfigurationQuery }],
    });

    if (get(result, 'data.updateBookingConfiguration')) {
      setOpen(false);
      window.location.reload();
    }
  };

  return (
    <Query query={bookingConfigurationQuery}>
      {({ loading, data = {} }) => (
        <div>
          <IconButton onClick={handleOpen} style={style}>
            <SettingsIcon />
          </IconButton>
          {open && (
            <RouterlessModal
              title={'Block Schedule Configuration'}
              onClose={() => setOpen(false)}
              fullWindow={window.innerWidth < breakpoint.md}
            >
              {!loading ? (
                <Form
                  key={formKey}
                  autoFocus
                  initialValue={mapInput(get(data, 'bookingConfiguration'))}
                  input={BlockScheduleConfigInput}
                  label={'Save'}
                  onSubmit={handleSubmit}
                  onCancel={() => setOpen(false)}
                />
              ) : (
                <CenteredSpinner />
              )}
            </RouterlessModal>
          )}
        </div>
      )}
    </Query>
  );
};

export default BlockScheduleConfig;
